// frontend/src/components/admin/CalenderWidget.jsx
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { format, isValid, parseISO } from 'date-fns';

const CalendarWidget = ({ events = [] }) => {
  // Helper function to safely parse and validate dates
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    
    let parsedDate;
    
    // Try different parsing methods
    if (typeof dateValue === 'string') {
      // Try parsing as ISO string first
      parsedDate = parseISO(dateValue);
      
      // If that fails, try regular Date constructor
      if (!isValid(parsedDate)) {
        parsedDate = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      parsedDate = dateValue;
    } else {
      return null;
    }
    
    // Return valid date or null
    return isValid(parsedDate) ? parsedDate : null;
  };

  // Helper function to safely format dates
  const safeFormat = (dateValue, formatString = 'yyyy-MM-dd') => {
    const parsedDate = parseDate(dateValue);
    if (!parsedDate) return null;
    
    try {
      return format(parsedDate, formatString);
    } catch (error) {
      console.warn('Date formatting error:', error, 'for value:', dateValue);
      return null;
    }
  };

  // Filter and transform events, removing any with invalid dates
  const calendarEvents = events
    .filter(event => {
      if (!event) return false;
      
      // Check if we have at least one valid date field
      const hasValidStartDate = parseDate(event.startDate || event.date);
      const hasValidEndDate = parseDate(event.endDate || event.date);
      
      return hasValidStartDate || hasValidEndDate;
    })
    .map(event => {
      const startDate = parseDate(event.startDate || event.date);
      const endDate = parseDate(event.endDate || event.date || event.startDate);
      
      // Format dates safely
      const formattedStartDate = safeFormat(startDate);
      const formattedEndDate = safeFormat(endDate);
      
      // Skip if we couldn't format the dates
      if (!formattedStartDate) return null;
      
      return {
        title: event.title || 'Untitled Event',
        start: formattedStartDate,
        end: formattedEndDate || formattedStartDate, // Use start date as fallback
        extendedProps: {
          location: event.location || '',
          eventType: event.eventType || '',
          description: event.description || ''
        }
      };
    })
    .filter(event => event !== null); // Remove null events

  const eventContent = (eventInfo) => {
    return (
      <div className="fc-event-content">
        <div className="font-medium text-sm">{eventInfo.event.title}</div>
        {eventInfo.event.extendedProps.location && (
          <div className="text-xs truncate opacity-90">
            📍 {eventInfo.event.extendedProps.location}
          </div>
        )}
      </div>
    );
  };

  const handleEventClick = (clickInfo) => {
    // You can add event click handling here
    console.log('Event clicked:', clickInfo.event);
  };

  const handleDateClick = (clickInfo) => {
    // You can add date click handling here
    console.log('Date clicked:', clickInfo.dateStr);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {calendarEvents.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Events</h3>
          <p className="text-sm text-gray-500">No events are currently scheduled.</p>
        </div>
      ) : (
        <div className="p-4">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={calendarEvents}
            headerToolbar={{
              left: 'title',
              center: '',
              right: 'prev,next today'
            }}
            height={500}
            eventContent={eventContent}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            eventClassNames="cursor-pointer hover:opacity-90 transition-opacity"
            dayMaxEvents={3}
            moreLinkClick="popover"
            eventDisplay="block"
            displayEventTime={false}
            // Styling
            themeSystem="standard"
            eventBackgroundColor="#3b82f6"
            eventBorderColor="#2563eb"
            eventTextColor="#ffffff"
            // Handle errors gracefully
            eventDidMount={(info) => {
              // Add tooltip or additional styling here if needed
              info.el.setAttribute('title', info.event.title);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarWidget;